import React from 'react';
import GenericMasterHeader from './GenericMasterHeader';
import OSSearch from "../select/OSSearch";
import { Ingredient } from "../types/Types";
import { Copy } from "lucide-react";
import OSNormativStatusBadge, { OSNormativStatus } from "./OSNormativStatusBadge";

interface IngredientMasterHeaderProps {
    isCreating: boolean;
    isSaving: boolean;
    hasChanges?: boolean;
    saveDisabled?: boolean;
    saveDisabledTitle?: string;
    onReset: () => void;
    onSave: () => void;
    onDelete?: () => void;
    deleteDisabled?: boolean;
    deleteDisabledTitle?: string;
    onArchive?: () => void;
    hasIngredient?: boolean;
    search: string;
    setSearch: (val: string) => void;
    ingredients: Ingredient[];
    loading: boolean;
    page: number;
    totalPages: number;
    absoluteTotal: number;
    setPage: (p: number) => void;
    onIngredientSelect?: (ingredient: Ingredient) => void;
    ingredientCode?: string;
    ingredientName?: string;
    currentIngredientId?: string;
    currentIngredientCode?: string; // FIX 1: explicit code for stable toolbar display
    onPrintPdf?: () => void;
    renderExcelExport?: () => React.ReactNode;
    onModalOpen?: () => void;
    onCopyIngredients?: () => void;
    onNewVersion?: () => void;
    /** Нов / Нацрт / Подготвен / Одобрен / Архивиран — shown after the ident in the title bar. */
    normativStatus?: OSNormativStatus;
    children?: React.ReactNode;
}

const OSMasterHeader: React.FC<IngredientMasterHeaderProps> = ({
    isCreating,
    isSaving,
    hasChanges = true,
    saveDisabled,
    saveDisabledTitle,
    onReset,
    onSave,
    onDelete,
    deleteDisabled,
    deleteDisabledTitle,
    onArchive,
    hasIngredient,
    search,
    setSearch,
    ingredients,
    loading,
    page,
    totalPages,
    absoluteTotal,
    setPage,
    onIngredientSelect,
    ingredientCode,
    ingredientName,
    currentIngredientId,
    currentIngredientCode,
    onPrintPdf,
    renderExcelExport,
    onModalOpen,
    onCopyIngredients,
    onNewVersion,
    normativStatus,
    children,
}) => {
    // The status pill sits in GenericMasterHeader's middle slot, right after the ident/name.
    const middle = normativStatus || children ? (
        <div className="flex items-center gap-3 min-w-0">
            {normativStatus && <OSNormativStatusBadge status={normativStatus} />}
            {children}
        </div>
    ) : undefined;

    return (
        <GenericMasterHeader
            entityLabel={ingredientCode || ''}
            entityCode={ingredientName}
            entityId={currentIngredientId}
            shortcutCategory="ingredients"
            isCreating={isCreating}
            isSaving={isSaving}
            hasChanges={hasChanges}
            hasEntity={hasIngredient}
            onSave={onSave}
            saveDisabled={saveDisabled}
            saveDisabledTitle={saveDisabledTitle}
            onNew={onReset}
            onDelete={onDelete}
            deleteDisabled={deleteDisabled}
            deleteDisabledTitle={deleteDisabledTitle}
            onArchive={onArchive}
            onPrintPdf={onPrintPdf}
            renderExcelExport={renderExcelExport}
            renderActionsLeft={onNewVersion ? () => (
                <button
                    type="button"
                    onClick={onNewVersion}
                    disabled={isSaving || !hasIngredient}
                    className="inline-flex h-7 items-center gap-1 rounded-lg px-2 text-xs font-medium text-blue-600 transition-all hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:text-gray-400"
                    title="Нова верзија"
                >
                    <Copy size={14} />
                    Нова верзија
                </button>
            ) : undefined}
            children={middle}
            renderSearch={() => (
                <div className="flex items-center gap-1 min-w-0">
                    <OSSearch
                        search={search}
                        setSearch={setSearch}
                        ingredients={ingredients}
                        loading={loading}
                        page={page}
                        totalPages={totalPages}
                        absoluteTotal={absoluteTotal}
                        setPage={setPage}
                        onSelect={onIngredientSelect}
                        currentIngredientId={currentIngredientId}
                        currentIngredientCode={currentIngredientCode}
                        onModalOpen={onModalOpen}
                    />
                    {onCopyIngredients && (
                        <button
                            type="button"
                            onClick={onCopyIngredients}
                            className="p-1.5 h-9 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 flex items-center justify-center flex-shrink-0"
                            disabled={isSaving}
                            title="Копирај норматив"
                        >
                            <Copy size={20} />
                        </button>
                    )}
                </div>
            )}
        />
    );
};

export default OSMasterHeader;
